// src/Routes.jsx
import React from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import ScrollToTop from "./components/ui/ScrollToTop.jsx";
import ErrorBoundary from "./components/ui/ErrorBoundary.jsx";

// Layouts
import Layout from "./Layout.jsx";
import LayoutOperations from "./LayoutOperations.jsx";

// 🔐 Auth desde AppGate (FUENTE ÚNICA)
import { useAuth } from "./components/system/AppGate";

// Pages
import Login from "./pages/auth/Login.jsx";
import SetPasswordPage from "./pages/auth/SetPassword.jsx";
import OfficeChurchHome from "./pages/officechurch-home";
import ClientAnalytics from "./pages/client-analytics";
import OffersPage from "./pages/offers";
import OffersStatsPage from "./pages/offers-stats";
import ClientReportPage from "./pages/client-360-report";
import PurchaseOrdersPage from "./pages/purchase-orders";
import IngresosPage from "./pages/ingresos";
import FacturacionPage from "./pages/facturacion";
import FacturacionGerenciaPage from "./pages/facturacion-gerencia";
import FacturacionInbox from "./pages/contabilidad/FacturacionInbox.jsx";
import IntakeReceiptPage from "./pages/ingresos/IntakeReceiptPage.jsx";
import IntakesHistoryPage from "./pages/ingresos/IntakesHistoryPage";
import SimpleInstrumentForm from "./pages/simple-instrument-form/index.jsx";


import OperationsOverview from "./pages/operations-overview";
import ServiceOrdersPage from "./pages/service-orders";
import InstrumentDetails from "./pages/instrument-details";
import LongitudinalCapture from "./pages/dimensional-longitudinal/LongitudinalCapture.jsx";
import DimensionalCalibrationPage from "./pages/dimensional-calibration";
import EnvironmentalCalibrationPage from "./pages/environmental-calibration";
import AnalyticalCalibrationPage from "./pages/analytical-calibration";
import PressureManometerTemplatePage from "./pages/pressure-manometer-template";
import PressureManometerPilotPage from "./pages/pressure-manometer-pilot";
import ElectricalCalibrationPage from "./pages/electrical-calibration";
import TemperatureCalibrationPage from "./pages/temperature-calibration";
import MassFlowCalibrationPage from "./pages/flow-calibration";
import MassCalibrationPage from "./pages/mass-calibration";
import ForceCalibrationPage from "./pages/force-calibration";
import GasCalibrationPage from "./pages/gas-calibration";
import SafetyValveCalibrationPage from "./pages/safety-valve-calibration";
import CheckValveCalibrationPage from "./pages/check-valve-calibration";
import CertificateApprovalsPage from "./pages/certificate-approvals";
import CertificatePublicPage from "./pages/certificate-public";
import CertificateSearchPage from "./pages/certificate-search";
import CertificateArchivePage from "./pages/certificate-archive";
import PortalLoginPage from "./pages/portal/PortalLogin";
import PortalResetPage from "./pages/portal/PortalReset";
import PortalPage from "./pages/portal";
import PortalUsersAdminPage from "./pages/admin/portal-users";

import QualityCompliancePage from "./pages/quality-compliance";
import ReferenceStandardInstrumentsPage from "./pages/reference-standard-instruments";
import NotFound from "./pages/quality-compliance/NotFound.jsx";

// Admin
import TechniciansAdminPage from "./pages/admin/technicians";
import UsersAdminPage from "./pages/admin/users";
import AdminExportsPage from "./pages/admin/exports";
import InstrumentDuplicatesAdminPage from "./pages/admin/instrument-duplicates";
import SecretKillSwitchLab from "./pages/admin/SecretKillSwitchLab.jsx";

/**
 * 🔐 Ruta protegida
 * ❗ NO habla con Supabase
 * ❗ Usa SOLO AppGate
 */
function ProtectedRoute({ children }) {
  const { user, role, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-300">
        Verificando sesión…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Un usuario del portal de clientes no navega la app interna.
  if (role === "client") {
    return <Navigate to="/portal" replace />;
  }

  // El auxiliar administrativo trabaja únicamente dentro del circuito de
  // Ingresos. Este control también cubre accesos por URL pegada manualmente.
  if (role === "administrative_assistant") {
    const allowed =
      location.pathname === "/ingresos" ||
      location.pathname.startsWith("/ingresos/") ||
      location.pathname === "/simple-instrument-form";

    if (!allowed) {
      return <Navigate to="/ingresos" replace />;
    }
  }

  // El jefe de laboratorio opera y controla calibraciones, pero no tiene
  // permisos de Facturación. También bloquea accesos por URL directa.
  if (
    role === "lab_manager" &&
    (location.pathname === "/facturacion" ||
      location.pathname.startsWith("/facturacion/") ||
      location.pathname.startsWith("/contabilidad/facturacion"))
  ) {
    return <Navigate to="/ops/my-pos" replace />;
  }

  return children;
}

function RoleProtectedRoute({ children, allowedRoles }) {
  const { user, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-300">
        Verificando permisos…
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!allowedRoles.includes(role)) {
    const fallback =
      role === "technician"
        ? "/ops/my-pos"
        : role === "lab_manager"
          ? "/ops/my-pos"
        : role === "accountant"
          ? "/contabilidad/facturacion"
          : role === "administrative_assistant"
            ? "/ingresos"
            : role === "commercial"
              ? "/offers"
              : "/";
    return <Navigate to={fallback} replace />;
  }

  return children;
}

// Ruta del portal de clientes: exige sesión con rol 'client'.
// El staff que caiga acá vuelve a la app interna (vería TODO por RLS y es confuso).
function PortalRoute({ children }) {
  const { user, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-300">
        Verificando sesión…
      </div>
    );
  }

  if (!user) return <Navigate to="/portal/login" replace />;
  if (role && role !== "client") return <Navigate to="/" replace />;

  return children;
}

const Routes = () => (
  <BrowserRouter>
    <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* =========================
            RUTAS PÚBLICAS
        ========================== */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/set-password" element={<SetPasswordPage />} />
        <Route path="/index.html" element={<OfficeChurchHome />} />
        <Route path="/inicio" element={<OfficeChurchHome />} />
        <Route path="/officechurch" element={<OfficeChurchHome />} />

        {/* Certificado por QR: la abre el cliente escaneando el sticker (sin login) */}
        <Route path="/c/:slug" element={<CertificatePublicPage />} />

        {/* Portal de clientes */}
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/reset" element={<PortalResetPage />} />
        <Route
          path="/portal"
          element={
            <PortalRoute>
              <PortalPage />
            </PortalRoute>
          }
        />

        {/* Ruta secreta sin layout */}
        <Route path="/_admin/lock" element={<SecretKillSwitchLab />} />

        {/* =========================
            LAYOUT COMERCIAL / GENERAL
        ========================== */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Comercial */}
          <Route path="/" element={<ClientAnalytics />} />
          <Route path="/client-analytics" element={<ClientAnalytics />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/offers-stats" element={<OffersStatsPage />} />
          <Route path="/client-360" element={<ClientReportPage />} />
          <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="/ingresos" element={<IngresosPage />} />
          <Route
            path="/facturacion"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "accountant"]}>
                <FacturacionPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/facturacion/laboratorio"
            element={
              <RoleProtectedRoute allowedRoles={["admin"]}>
                <FacturacionGerenciaPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/contabilidad/facturacion"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "accountant"]}>
                <FacturacionInbox />
              </RoleProtectedRoute>
            }
          />

          {/* ✅ Ingreso rápido de instrumentos (OC/OS) */}
          <Route
            path="/simple-instrument-form"
            element={<SimpleInstrumentForm />}
          />

          <Route path="/ingresos/comprobante" element={<IntakeReceiptPage />} />
          <Route path="/ingresos/historial" element={<IntakesHistoryPage />} />

          {/* Calidad */}
          <Route
            path="/quality-compliance"
            element={<QualityCompliancePage />}
          />
          <Route
            path="/quality/reference-standards"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "lab_manager"]}>
                <ReferenceStandardInstrumentsPage />
              </RoleProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin/users"
            element={<UsersAdminPage />}
          />
          <Route
            path="/admin/technicians"
            element={<TechniciansAdminPage />}
          />
          <Route
            path="/admin/exports"
            element={<AdminExportsPage />}
          />
          <Route
            path="/admin/instrument-duplicates"
            element={<InstrumentDuplicatesAdminPage />}
          />
          <Route
            path="/admin/portal-users"
            element={<PortalUsersAdminPage />}
          />
        </Route>

        {/* =========================
            LAYOUT OPERACIONES (AISLADO)
        ========================== */}
        <Route
          path="/service-orders/:id"
          element={
            <ProtectedRoute>
              <ServiceOrdersPage />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <LayoutOperations />
            </ProtectedRoute>
          }
        >
          {/* Overview técnico */}
          <Route path="/operations-overview" element={<OperationsOverview />} />

          {/* Aliases */}
          <Route path="/ops" element={<Navigate to="/ops/my-pos" replace />} />
          <Route path="/ops/my-pos" element={<OperationsOverview />} />

          {/* Órdenes de servicio */}
          <Route path="/ops/service-orders" element={<ServiceOrdersPage />} />
          <Route path="/ops/service-orders/:id" element={<ServiceOrdersPage />} />

          {/* Instrumentos / calibración (alias legacy) */}
          <Route
            path="/ops/instrument-details/:instrumentId"
            element={<InstrumentDetails />}
          />

          <Route
            path="/ops/calibrations/dimensional/longitudinal/:instrumentId"
            element={<LongitudinalCapture />}
          />
          <Route
            path="/ops/calibrations/dimensional/:profile/:instrumentId"
            element={<DimensionalCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/environmental/:profile/:instrumentId"
            element={<EnvironmentalCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/analytical/:profile/:instrumentId"
            element={<AnalyticalCalibrationPage />}
          />
        

          <Route
            path="/ops/calibrations/pressure/manometer/:instrumentId"
            element={<PressureManometerPilotPage />}
          />
          <Route
            path="/ops/calibrations/pressure/manometer/:instrumentId/template"
            element={<PressureManometerTemplatePage />}
          />
          <Route
            path="/ops/calibrations/electrical/multimeter/:instrumentId"
            element={<ElectricalCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/temperature/contact/:instrumentId"
            element={<TemperatureCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/temperature/infrared/:instrumentId"
            element={<TemperatureCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/temperature/sensor/:instrumentId"
            element={<TemperatureCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/temperature/chamber/:instrumentId"
            element={<TemperatureCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/flow/mass/:instrumentId"
            element={<MassFlowCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/flow/electromagnetic/:instrumentId"
            element={<MassFlowCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/flow/mechanical-totalizer/:instrumentId"
            element={<MassFlowCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/flow/ultrasonic/:instrumentId"
            element={<MassFlowCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/flow/rotameter/:instrumentId"
            element={<MassFlowCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/mass/balance/:instrumentId"
            element={<MassCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/force/torque/:instrumentId"
            element={<ForceCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/gas/detector/:instrumentId"
            element={<GasCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/valve/safety/:instrumentId"
            element={<SafetyValveCalibrationPage />}
          />
          <Route
            path="/ops/calibrations/valve/check/:instrumentId"
            element={<CheckValveCalibrationPage />}
          />

          {/* Bandeja del jefe: aprobar y emitir certificados */}
          <Route
            path="/ops/certificate-approvals"
            element={<CertificateApprovalsPage />}
          />

          {/* Buscador de certificados e instrumentos (jefe + administrativos) */}
          <Route path="/ops/certificate-archive" element={
            <RoleProtectedRoute allowedRoles={["admin", "lab_manager"]}>
              <CertificateArchivePage />
            </RoleProtectedRoute>
          } />
          <Route
            path="/ops/certificates"
            element={<CertificateSearchPage />}
          />
</Route>

        {/* =========================
            🔑 RUTA CLAVE (FUERA DE COMERCIAL)
        ========================== */}
        <Route
          element={
            <ProtectedRoute>
              <LayoutOperations />
            </ProtectedRoute>
          }
        >
          <Route
            path="/instrument-details/:instrumentId"
            element={<InstrumentDetails />}
          />
        </Route>

        {/* =========================
            404
        ========================== */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </ErrorBoundary>
  </BrowserRouter>
);

export default Routes;


